import { useRef, useState, useEffect } from 'react';
import SelectSymbol from '../../../components/SelectSymbol';
import LogButton from '../../../components/Logs/LogButton';
import LogView from '../../../components/Logs/LogView';
import DateTime from '../../../components/DateTime';
import AutomationsList from './AutomationsList';
import { getAllAutomations, doBacktest, getBacktestProgress, pauseBacktest, resumeBacktest } from '../../../services/AutomationsService';
import { getSymbol } from '../../../services/SymbolsService';
import { getMemoryIndex } from '../../../services/BeholderService';
import ResultsTable from './ResultsTable';
import ResultsButton from './ResultsButton';

function BacktestModal() {

    const DEFAULT_BACKTEST = {
        symbol: '',
        startBase: 0,
        startQuote: 0,
        startTime: 0,
        startDate: '',
        endDate: '',
        endTime: 0,
        automationIds: []
    }

    const [testing, setTesting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [paused, setPaused] = useState(false);
    const [error, setError] = useState('');

    const [backtest, setBacktest] = useState(DEFAULT_BACKTEST);
    const [automations, setAutomations] = useState([]);

    const btnClose = useRef('');
    const btnTest = useRef('');

    function onSubmit(event) {
        if (testing) return;

        // validação dos campos obrigatórios
        if (!backtest.startBase || parseFloat(backtest.startBase) <= 0) {
            setError('Start Base é obrigatório e deve ser maior que 0.');
            return;
        }

        if (!backtest.startQuote || parseFloat(backtest.startQuote) <= 0) {
            setError('Start Quote é obrigatório e deve ser maior que 0.');
            return;
        }

        if (!backtest.automationIds || backtest.automationIds.length === 0) {
            setError('Selecione pelo menos uma automação.');
            return;
        }

        setError('');
        setTesting(true);
        setProgress(0);
        setPaused(false);
        doBacktest(backtest)
            .then(result => {
                result.automationIds = backtest.automationIds;
                setBacktest({ ...result, startTime: backtest.startTime, endTime: backtest.endTime });
                setTesting(false);
                setProgress(100);
                setPaused(false);
                setShowLogs(false);
                setShowResults(true);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err);
                setError(err.response ? err.response.data : err.message);
                setTesting(false);
                setProgress(0);
                setPaused(false);
            })
    }

    function onInputChange(event) {
        setBacktest(prevState => ({ ...prevState, [event.target.id]: event.target.value }));
    }

    useEffect(() => {
        getAllAutomations()
            .then(results => {
                results = backtest.symbol
                    ? results.filter(a => !a.schedule && !a.name.startsWith('GRID ') && a.symbol === backtest.symbol)
                    : results.filter(a => !a.schedule && !a.name.startsWith('GRID '));

                setAutomations(results);
                setShowLogs(false);
                setShowResults(false);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err);
                setError(err.response ? err.response.data : err.message);
            })
    }, [backtest.symbol])

    useEffect(() => {
        if (!testing) return;

        const interval = setInterval(() => {
            getBacktestProgress()
                .then(data => {
                    if (typeof data.progress === 'number') {
                        setProgress(data.progress);
                        if (typeof data.paused === 'boolean') {
                            setPaused(data.paused);
                        }
                        // se chegou a 100%, para o polling (o .then do doBacktest vai finalizar)
                        if (data.progress >= 100) {
                            clearInterval(interval);
                            setPaused(false);
                        }
                    }
                })
                .catch(() => { });
        }, 500); // polling mais frequente (500ms)

        return () => clearInterval(interval);
    }, [testing])

    function onPauseClick() {
        pauseBacktest()
            .then(() => {
                setPaused(true);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err);
                setError(err.response ? err.response.data : err.message);
            });
    }

    function onResumeClick() {
        resumeBacktest()
            .then(() => {
                setPaused(false);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err);
                setError(err.response ? err.response.data : err.message);
            });
    }

    const [showLogs, setShowLogs] = useState(false);
    function onLogClick(event) {
        if (!showLogs) setShowResults(false);
        setShowLogs(!showLogs);
    }

    const [showResults, setShowResults] = useState(false);
    function onViewResultsClick(event) {
        if (!showResults) setShowLogs(false);
        setShowResults(!showResults);
    }

    function onSymbolChange(event) {
        if (!event.target.value) return;

        const backtest = { ...DEFAULT_BACKTEST };
        backtest.symbol = event.target.value;

        let symbol;
        getSymbol(backtest.symbol)
            .then(symbolObj => {
                symbol = symbolObj;
                return getMemoryIndex(symbol.base, 'WALLET', null);
            })
            .then(base => {
                // se não encontrou na carteira, usa valor padrão sugerido
                backtest.startBase = base && base > 0 ? base : 0.1;
                return getMemoryIndex(symbol.quote, 'WALLET', null);
            })
            .then(quote => {
                // se não encontrou na carteira, usa valor padrão sugerido baseado no símbolo
                if (!quote || quote <= 0) {
                    // valores sugeridos padrão: 1000 USDT para pares com USDT, 100 para outros
                    const isUSDT = backtest.symbol.includes('USDT');
                    backtest.startQuote = isUSDT ? 1000 : 100;
                } else {
                    backtest.startQuote = quote;
                }
                setBacktest({ ...backtest });
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err);
                // mesmo em erro, define valores padrão
                const isUSDT = backtest.symbol.includes('USDT');
                backtest.startBase = 0.1;
                backtest.startQuote = isUSDT ? 1000 : 100;
                setBacktest({ ...backtest });
                setError(err.response ? err.response.data : err.message);
            })
    }

    function onUseSuggestedValues() {
        if (!backtest.symbol) {
            setError('Selecione um símbolo primeiro.');
            return;
        }

        const isUSDT = backtest.symbol.includes('USDT');
        setBacktest(prevState => ({
            ...prevState,
            startBase: 0.1,
            startQuote: isUSDT ? 1000 : 100
        }));
    }

    useEffect(() => {
        const modal = document.getElementById('modalBacktest');
        modal.addEventListener('hidden.bs.modal', (event) => {
            setBacktest({ ...DEFAULT_BACKTEST });
            setShowLogs(false);
        })
    }, [])

    return (
        <div className="modal fade" id="modalBacktest" tabIndex="-1" role="dialog" aria-labelledby="modalTitleNotify" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <p className="modal-title" id="modalTitleNotify">Backtest</p>
                        <button ref={btnClose} type="button" className="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="form-group">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label htmlFor="symbol">Symbol:</label>
                                        <SelectSymbol symbol={backtest.symbol} onChange={onSymbolChange} onlyFavorites={true} />
                                    </div>
                                </div>
                            </div>
                            {
                                !showLogs && !showResults
                                    ? (
                                        <>
                                            <AutomationsList automationIds={backtest.automationIds} data={automations} onChange={onInputChange} />
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="startBase">
                                                            Start Base (moeda base - ex: BTC): 
                                                            <span className="badge bg-info ms-2" data-bs-toggle="tooltip" title="Quantidade inicial da moeda base na carteira de simulação">?</span>
                                                        </label>
                                                        <input type="number" step="any" id="startBase" className="form-control" placeholder="0.1" value={backtest.startBase || ''} onChange={onInputChange} />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="startQuote">
                                                            Start Quote (moeda de cotação - ex: USDT): 
                                                            <span className="badge bg-info ms-2" data-bs-toggle="tooltip" title="Quantidade inicial da moeda de cotação na carteira de simulação">?</span>
                                                        </label>
                                                        <input type="number" step="any" id="startQuote" className="form-control" placeholder="1000" value={backtest.startQuote || ''} onChange={onInputChange} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-12 mb-3">
                                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onUseSuggestedValues}>
                                                        <svg className="icon icon-xs me-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" /></svg>
                                                        Usar valores sugeridos
                                                    </button>
                                                    <small className="text-muted ms-2">
                                                        (Preenche automaticamente com valores padrão para teste)
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-12 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="startTime">Start Date:</label>
                                                        <DateTime id="startTime" onChange={onInputChange} date={backtest.startDate || ''} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-12 mb-3">
                                                    <div className="form-group">
                                                        <label htmlFor="endTime">End Date:</label>
                                                        <DateTime id="endTime" onChange={onInputChange} date={backtest.endDate || ''} />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )
                                    : <></>
                            }
                            {
                                !showLogs && showResults
                                    ? <ResultsTable data={backtest} />
                                    : <></>
                            }
                            {
                                showLogs && !showResults
                                    ? <LogView file={"backtest-" + backtest.userId} />
                                    : (
                                        <>
                                        </>
                                    )
                            }
                        </div>
                    </div>
                    <div className="modal-footer">
                        {
                            error
                                ? <div className="alert alert-danger mt-1 col-9 py-1">{error}</div>
                                : <></>
                        }
                        <LogButton id={backtest.id} onClick={onLogClick} />
                        <ResultsButton id={backtest.id} onClick={onViewResultsClick} />
                        <div className="d-flex align-items-center">
                            <button ref={btnTest} type="button" className="btn btn-sm btn-primary me-2" onClick={onSubmit} disabled={!backtest.automationIds.length || testing}>
                                {
                                    testing
                                        ? "Testing..."
                                        : "Start Test"
                                }
                            </button>
                            {
                                testing
                                    ? (
                                        <>
                                            {
                                                paused
                                                    ? (
                                                        <button type="button" className="btn btn-sm btn-success me-2" onClick={onResumeClick}>
                                                            Continuar
                                                        </button>
                                                    )
                                                    : (
                                                        <button type="button" className="btn btn-sm btn-warning me-2" onClick={onPauseClick}>
                                                            Pausar
                                                        </button>
                                                    )
                                            }
                                            <span className="text-muted small">{progress}% {paused ? '(Pausado)' : ''}</span>
                                        </>
                                    )
                                    : <></>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default BacktestModal;
